import { isApiError } from '@repletoryreach/sdk-api';

/**
 * The single source of truth for password rules in the tenant console.
 *
 * Since 2 October 2026 identity asks for at least 12 characters and nothing
 * else about their kind (no capital, digit or symbol rule), and refuses a
 * password that is one of the most common leaked ones
 * (`services/identity/Program.cs`, `BreachedPasswordValidator`). Length is
 * the only rule this console can check; the leaked-password check happens on
 * the server, and its refusal arrives with `reason: "breached"`
 * (see {@link passwordRefusalMessage}).
 *
 * Three surfaces set a password: signup, reset-password and Settings →
 * Security, and each reads its rules from here.
 */

export interface PasswordRule {
  /** Catalog key describing the rule. */
  key: string;
  met: boolean;
}

export const PASSWORD_MIN_LENGTH = 12;

/** Evaluate every rule against a candidate password, in display order. */
export function passwordRules(password: string): PasswordRule[] {
  return [{ key: 'password.rules.length', met: password.length >= PASSWORD_MIN_LENGTH }];
}

/** True when every rule this console can check passes. The server may still refuse a common leaked password. */
export function isPasswordValid(password: string): boolean {
  return passwordRules(password).every((rule) => rule.met);
}

/** 0–100, for a strength meter: length is what makes a password strong, so 20 characters fill it. */
export function passwordStrength(password: string): number {
  if (!password) return 0;
  return Math.min(100, Math.round((password.length / 20) * 100));
}

/** The sentence for a password identity refused, or null when the refusal was about something else. */
export function passwordRefusalMessage(err: unknown, t: (key: string, vars?: Record<string, string | number>) => string): string | null {
  if (!isApiError(err)) return null;
  const reason = (err.body as { error?: { reason?: string } } | null)?.error?.reason;
  if (reason === 'breached') return t('password.errors.breached');
  if (reason === 'too_short') return t('password.errors.tooShort');
  return null;
}
